import React, { memo, useEffect, useState } from "react";
import { GoogleMap, LoadScript, StreetViewPanorama, Marker, InfoWindow, Polyline } from "@react-google-maps/api";
import { Skeleton } from "@mui/material";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import BoyIcon from '@mui/icons-material/Boy';
import Human from "assets/svg/humanMarker.svg";

import styles from "./MapStreetView.module.scss"
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
dayjs.extend(duration);
dayjs.extend(utc);
dayjs.extend(timezone);

const MapWithStreetView = ({ location = {},submitter={}, timeToComplete = {}, submitterGeoCoordinates, loader }) => {
    const API_KEY = "AIzaSyAeaVCOcEf0xlauW7fo_okZt_hChJgZ-l8"; // Replace with your API key

    const { latitude, longitude } = location;
    const position = { lat: Number(latitude), lng: Number(longitude) };

    const [streetViewVisible, setStreetViewVisible] = useState(false);
    const [streetViewPosition, setStreetViewPosition] = useState(position);
    const [showInfoWindow, setShowInfoWindow] = useState(false);
    const [address, setAddress] = useState(null);
    const [loading, setLoading] = useState(false);
    const [zoomLevel, setZoomLevel] = useState(15); // Set initial zoom level

    const handleStreetView = (latLng) => {
        setStreetViewPosition({ lat: latLng.lat(), lng: latLng.lng() });
        setStreetViewVisible(true);
    };

    const parseDuration = (durationStr) => {
        const [hours, minutes, seconds] = durationStr.split(':').map(Number);
        return `${hours} hr, ${minutes} mins, ${seconds} secs`
    }

    useEffect(() => {
        const getAddressFromLatLng = async (lat, lng) => {
            setLoading(true);

            if (!window.google || !window.google.maps) return;
            const geocoder = new window.google.maps.Geocoder();
            const latLng = new window.google.maps.LatLng(lat, lng);

            try {
                const results = await new Promise((resolve, reject) => {
                    geocoder.geocode({ location: latLng }, (results, status) => {
                        if (status === 'OK') {
                            resolve(results[0].formatted_address); // Get formatted address
                        } else {
                            reject('Geocoder failed due to: ' + status);
                        }
                    });
                });
                setAddress(results);
                setLoading(false);
            } catch (error) {
                console.error(error);
                setLoading(false);
            }
        };

        if (latitude && longitude)
            getAddressFromLatLng(latitude, longitude);
    }, [latitude, longitude]);

    const [map, setMap] = useState(null);

    useEffect(() => {
        if (map && submitterGeoCoordinates && submitterGeoCoordinates.latitude && submitterGeoCoordinates.longitude) {
            const submitterLocation = new window.google.maps.LatLng(submitterGeoCoordinates.latitude, submitterGeoCoordinates.longitude);
            const mapCenter = new window.google.maps.LatLng(latitude, longitude);

            const bounds = new window.google.maps.LatLngBounds();
            bounds.extend(mapCenter);  // Extend bounds to include the first marker
            bounds.extend(submitterLocation);  // Extend bounds to include the submitter's marker

            map.fitBounds(bounds);  // Fit map to bounds of both markers
        }
    }, [submitterGeoCoordinates, latitude, longitude, map]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: ".5rem" }}>
            {!loading && !loader ?
                <h4>{address}</h4>
                :
                <Skeleton height={60} variant="text" />
            }

            <LoadScript googleMapsApiKey={API_KEY} loadingElement={<Skeleton height={200} variant="rect" />} libraries={['geometry']}>
                <GoogleMap
                    mapContainerStyle={{ width: "100%", height: "200px" }}
                    center={position}
                    zoom={zoomLevel}  // Use dynamic zoom level
                    onClick={(e) => handleStreetView(e.latLng)}
                    options={{ streetViewControl: true }}
                    onLoad={(map) => {
                        setMap(map);  // Save map instance
                    }}
                >

                    {/* Marker with InfoWindow on hover */}
                    <Marker
                        position={position}
                        onMouseOver={() => setShowInfoWindow(true)}
                        onMouseOut={() => setShowInfoWindow(false)}
                        icon={{
                            url: Human,
                            // anchor: new window.google.maps.Point(16, 32)
                        }}
                    >
                        {showInfoWindow && (
                            <InfoWindow position={position} onCloseClick={() => setShowInfoWindow(false)}>
                                <div>
                                    <p style={{ margin: 0, fontWeight: "bold" }}>{address}</p>
                                </div>
                            </InfoWindow>
                        )}
                    </Marker>

                    {submitterGeoCoordinates && Object?.keys(submitterGeoCoordinates)?.length > 0 &&
                        <Marker
                            position={{
                                lat: submitterGeoCoordinates?.latitude,
                                lng: submitterGeoCoordinates?.longitude
                            }}
                        >
                        </Marker>
                    }

                    {/* Create a path between the two markers */}
                    <Polyline
                        path={[
                            { lat: submitterGeoCoordinates?.latitude, lng: submitterGeoCoordinates?.longitude },
                            position
                        ]}
                        geodesic={true}
                        options={{
                            strokeColor: "#ff2527",
                            strokeOpacity: 0.75,
                            strokeWeight: 2,
                            strokeDasharray: "5,5",
                            icons: [{ strokeDasharray: "5,5" }]
                        }}
                    />

                    {streetViewVisible && (
                        <StreetViewPanorama
                            position={streetViewPosition}
                            visible={streetViewVisible}
                            options={{ pov: { heading: 100, pitch: 0 } }}
                        />
                    )}
                </GoogleMap>
            </LoadScript>

            {loader ?
                <Skeleton height={50} variant="rect" />
                :
                <>
                    <h3 style={{margin:0}}>{`${submitter?.firstName} ${submitter?.lastName}`}</h3>
                    <ul className={styles.events_list}>
                        <li>
                            <div className={styles.action_label}><RadioButtonCheckedIcon sx={{ color: "#2ecc71" }} />Started</div>
                            <div className={styles.action_val}>{dayjs(timeToComplete?.startTime).subtract(5, 'hour').subtract(30, 'minute').format('MMM D, YYYY h:mm a [PST]')}</div>
                        </li>

                        <li className={styles.duration}>
                            {timeToComplete?.duration && parseDuration(timeToComplete?.duration)}
                        </li>

                        <li>
                            <div className={styles.action_label}><BoyIcon sx={{ color: "#0871c9" }} />Submitted</div>
                            <div className={styles.action_val}>
                                {dayjs(timeToComplete?.endTime).subtract(5, 'hour').subtract(30, 'minute').format('MMM D, YYYY h:mm a [PST]')}
                            </div>
                        </li>

                        <li className="action action_received">
                            <div className={styles.action_label}><RadioButtonCheckedIcon sx={{ color: "#0871c9" }} />Received</div>
                            <div className={styles.action_val}>
                                {dayjs(timeToComplete?.endTime).subtract(5, 'hour').subtract(30, 'minute').format('MMM D, YYYY h:mm a [PST]')}
                            </div>
                        </li>
                    </ul>
                </>
            }
        </div>
    );
};

export default memo(MapWithStreetView);